import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  validateQuarryStage,
  validateTransportStage,
  validateManufacturingStage,
  validateInstallationStage,
  validateUsePhaseStage,
  validateEndOfLifeStage,
  validateCompleteScenario,
  canProceedToNextStage,
  getValidationErrors,
  autoFixRocksBlasted
} from '../utils/scenarioValidation';

/**
 * Custom hook for managing scenario validation
 * 
 * @param {Object} scenarioData - The complete scenario data
 * @param {string} currentStage - The current stage being edited
 * @param {boolean} validateOnChange - Whether to validate on every change
 * @returns {Object} Validation state and helper functions
 */
export const useScenarioValidation = (
  scenarioData = {},
  currentStage = 'quarry',
  validateOnChange = true
) => {
  const [validationState, setValidationState] = useState({
    isValid: true,
    errors: {},
    warnings: [],
    touched: false
  });

  const [fieldErrors, setFieldErrors] = useState({});

  // Validate current stage
  const validateCurrentStage = useCallback(() => {
    if (!scenarioData || !currentStage) {
      return { isValid: true, errors: {}, warnings: [] };
    }

    let stageData;
    let previousStageData = null;

    switch (currentStage) {
      case 'quarry':
      case 'A1':
        stageData = scenarioData.quarry || {};
        return validateQuarryStage(stageData);

      case 'transport':
      case 'A2':
        stageData = scenarioData.transport || {};
        return validateTransportStage(stageData);

      case 'asphalt_manufacturing':
      case 'manufacturing':
      case 'A3':
        stageData = scenarioData.asphalt_manufacturing || scenarioData.manufacturing || {};
        previousStageData = scenarioData.quarry || {};
        return validateManufacturingStage(stageData, previousStageData);

      case 'transport_from_plant':
      case 'installation':
      case 'A4-A5':
        stageData = scenarioData.transport_from_plant || scenarioData.installation || {};
        return validateInstallationStage(stageData);

      case 'maintenance':
      case 'usePhase':
      case 'B2-B5':
        stageData = scenarioData.maintenance || scenarioData.usePhase || {};
        return validateUsePhaseStage(stageData);

      case 'end_of_life':
      case 'endOfLife':
      case 'C1-C4':
        stageData = scenarioData.end_of_life || scenarioData.endOfLife || {};
        return validateEndOfLifeStage(stageData);

      default:
        return { isValid: true, errors: {}, warnings: [] };
    }
  }, [scenarioData, currentStage]);

  // Validate entire scenario
  const validateAll = useCallback(() => {
    return validateCompleteScenario(scenarioData);
  }, [scenarioData]);

  // Check if can proceed to next stage (based on current validation)
  const canProceed = useMemo(() => {
    return validationState.isValid;
  }, [validationState.isValid]);

  // Validate on change
  useEffect(() => {
    if (validateOnChange) {
      const result = validateCurrentStage();
      setValidationState(prev => ({
        ...prev,
        ...result,
        touched: true
      }));
    }
  }, [scenarioData, currentStage, validateOnChange, validateCurrentStage]);

  // Mark as touched (for validation trigger)
  const markAsTouched = useCallback(() => {
    setValidationState(prev => ({
      ...prev,
      touched: true
    }));
  }, []);

  // Validate specific field
  const validateField = useCallback((fieldName, value, fieldLabel = '') => {
    const numValue = parseFloat(value);
    
    if (isNaN(numValue)) {
      return {
        isValid: false,
        error: `${fieldLabel || fieldName} must be a valid number`
      };
    }
    
    if (numValue < 0) {
      return {
        isValid: false,
        error: `${fieldLabel || fieldName} must be greater than or equal to 0`
      };
    }
    
    return { isValid: true, error: null };
  }, []);

  // Set field error
  const setFieldError = useCallback((fieldName, error) => {
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  }, []);

  // Clear field error
  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  // Handle field change with validation
  const handleFieldChange = useCallback((fieldName, value, fieldLabel = '') => {
    const validation = validateField(fieldName, value, fieldLabel);
    
    if (validation.isValid) {
      clearFieldError(fieldName);
    } else {
      setFieldError(fieldName, validation.error);
    }
    
    markAsTouched();
    
    return validation.isValid;
  }, [validateField, setFieldError, clearFieldError, markAsTouched]);

  // Auto-fix aggregate sum in quarry stage
  const handleAutoFixAggregates = useCallback(() => {
    if (currentStage === 'quarry' || currentStage === 'A1') {
      const fixedData = autoFixRocksBlasted(scenarioData.quarry || {});
      return fixedData;
    }
    return null;
  }, [currentStage, scenarioData]);

  // Get error message for specific field
  const getFieldError = useCallback((fieldName) => {
    return fieldErrors[fieldName] || validationState.errors[fieldName];
  }, [fieldErrors, validationState.errors]);

  // Check if specific field has error
  const hasFieldError = useCallback((fieldName) => {
    return !!(fieldErrors[fieldName] || validationState.errors[fieldName]);
  }, [fieldErrors, validationState.errors]);

  // Get validation status for UI display
  const getValidationStatus = useCallback(() => {
    const currentValidation = validateCurrentStage();
    const errorCount = Object.keys(currentValidation.errors || {}).length;
    const warningCount = (currentValidation.warnings || []).length;

    return {
      isValid: currentValidation.isValid,
      errorCount,
      warningCount,
      canProceed,
      message: currentValidation.isValid 
        ? 'All validations passed' 
        : `${errorCount} error(s) found`
    };
  }, [validateCurrentStage, canProceed]);

  // Reset validation state
  const resetValidation = useCallback(() => {
    setValidationState({
      isValid: true,
      errors: {},
      warnings: [],
      touched: false
    });
    setFieldErrors({});
  }, []);

  // Force validation (useful for submit handlers)
  const forceValidate = useCallback(() => {
    const result = validateCurrentStage();
    setValidationState({
      ...result,
      touched: true
    });
    return result.isValid;
  }, [validateCurrentStage]);

  return {
    // Validation state
    validationState,
    fieldErrors,
    canProceed,

    // Validation functions
    validateCurrentStage,
    validateAll,
    validateField,
    forceValidate,

    // Field-level helpers
    handleFieldChange,
    getFieldError,
    hasFieldError,
    setFieldError,
    clearFieldError,

    // Stage management
    markAsTouched,
    resetValidation,
    getValidationStatus,

    // Auto-fix utilities
    handleAutoFixAggregates
  };
};

/**
 * Custom hook for managing multi-stage wizard validation
 * 
 * @param {Object} scenarioData - The complete scenario data
 * @param {Array} stages - Array of stage names in order
 * @param {number} currentStageIndex - Index of current stage
 * @returns {Object} Wizard-specific validation state and helpers
 */
export const useWizardValidation = (
  scenarioData = {},
  stages = ['quarry', 'transport', 'manufacturing', 'installation'],
  currentStageIndex = 0
) => {
  const currentStage = stages[currentStageIndex];
  const [stageValidations, setStageValidations] = useState({});

  // Validate all stages up to current
  const validateUpToCurrent = useCallback(() => {
    const validations = {};
    let allValid = true;

    for (let i = 0; i <= currentStageIndex; i++) {
      const stageName = stages[i];
      const validation = getValidationErrors(stageName, scenarioData[stageName] || {});
      validations[stageName] = validation;
      
      if (!validation.isValid) {
        allValid = false;
      }
    }

    setStageValidations(validations);
    return { isValid: allValid, validations };
  }, [scenarioData, stages, currentStageIndex]);

  // Check if can navigate to specific stage
  const canNavigateToStage = useCallback((targetStageIndex) => {
    // Can always go back
    if (targetStageIndex <= currentStageIndex) {
      return true;
    }

    // Check if all previous stages are valid
    for (let i = 0; i < targetStageIndex; i++) {
      const stageName = stages[i];
      if (!canProceedToNextStage(stageName, scenarioData)) {
        return false;
      }
    }

    return true;
  }, [currentStageIndex, stages, scenarioData]);

  // Get stage validation status
  const getStageStatus = useCallback((stageName) => {
    const validation = stageValidations[stageName];
    
    if (!validation) {
      return 'unvalidated';
    }

    if (validation.isValid) {
      return 'valid';
    }

    return 'invalid';
  }, [stageValidations]);

  // Get progress percentage
  const getProgress = useCallback(() => {
    const validStages = Object.values(stageValidations).filter(v => v.isValid).length;
    return Math.round((validStages / stages.length) * 100);
  }, [stageValidations, stages]);

  return {
    stageValidations,
    validateUpToCurrent,
    canNavigateToStage,
    getStageStatus,
    getProgress
  };
};

export default useScenarioValidation;